/**
 * Expansion logic for the reasoning step timeline.
 *
 * Lives in its own module (not in ReasoningStepsList.tsx) so the component file
 * only exports components — keeping react-refresh/only-export-components happy.
 */

const hasContent = (step: { content?: string }): boolean =>
  !!step.content && step.content.trim().length > 0;

/**
 * Which steps should be auto-expanded by default.
 * - compact (inline processing card): the latest step stays open so the card
 *   reads as a tidy progress list and completed steps collapse to a single-line
 *   header. BUT the in-flight step is often a tool-only step with no prose yet
 *   (e.g. "research" while web searches run); collapsing everything else would
 *   blank out the reasoning the user was just reading. So when the latest step
 *   has no content of its own, we ALSO keep the most recent step that does have
 *   content expanded — its text stays visible at least until the live step
 *   produces its own.
 * - full (sidebar history): expand every step.
 */
export function getAutoExpandedStepIds(
  reasoningSteps: readonly { id: string; content?: string }[],
  compact: boolean
): Set<string> {
  if (compact) {
    const latest = reasoningSteps[reasoningSteps.length - 1];
    if (!latest) {
      return new Set<string>();
    }
    const ids = new Set([latest.id]);
    if (!hasContent(latest)) {
      for (let i = reasoningSteps.length - 2; i >= 0; i--) {
        if (hasContent(reasoningSteps[i])) {
          ids.add(reasoningSteps[i].id);
          break;
        }
      }
    }
    return ids;
  }
  return new Set(reasoningSteps.map(step => step.id));
}
